import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConsequenceReveal from '../shared/ConsequenceReveal';
import styles from './MisconceptionCatcherEngine.module.css';

const MisconceptionCatcherEngine = ({ gamifiedQuestion, onAnswer, accent = '#f4a261' }) => {
  const [selected, setSelected] = useState(null);
  const [showOutcome, setShowOutcome] = useState(false);
  const [sceneReady, setSceneReady] = useState(false);
  const [studentExpression, setStudentExpression] = useState('confused');

  const options = useMemo(() => gamifiedQuestion?.options?.slice(0, 4) || [], [gamifiedQuestion?.options]);
  const selectedOption = options.find((option) => option.label === selected);

  useEffect(() => {
    const timer = setTimeout(() => setSceneReady(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleSelect = (label) => {
    if (!sceneReady || selected) return;
    setSelected(label);

    // Update student expression based on choice
    const isCorrect = label === 'B'; // Typically B is correct for misconception questions
    setStudentExpression(isCorrect ? 'happy' : 'confused');

    setTimeout(() => setShowOutcome(true), 1000);
  };

  return (
    <div className={styles.engine} style={{ '--accent': accent }}>
      {/* SCENE AREA - Top */}
      <div className={styles.sceneArea}>
        <motion.svg
          viewBox="0 0 520 280"
          className={styles.sceneSvg}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          {/* Background */}
          <rect x="0" y="0" width="520" height="280" fill="#fafafa" rx="12" />

          {/* Floor */}
          <rect x="20" y="220" width="480" height="50" fill="#f0f0f0" stroke="#1a1a1a" strokeWidth="2" />

          {/* Whiteboard */}
          <motion.g
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <rect x="60" y="30" width="240" height="120" fill="#fff" stroke="#1a1a1a" strokeWidth="2.5" rx="4" />
            {/* Content on board - showing a misconception */}
            <line x1="80" y1="60" x2="280" y2="60" stroke="#e63946" strokeWidth="2" strokeDasharray="4" />
            <text x="170" y="55" textAnchor="middle" fontSize="12" fill="#e63946">Misconception?</text>
            <line x1="80" y1="85" x2="200" y2="85" stroke="#e0e0e0" strokeWidth="2" />
            <line x1="80" y1="105" x2="220" y2="105" stroke="#e0e0e0" strokeWidth="2" />
            {/* Arrow pointing to confusion */}
            <motion.path
              d="M 260 90 Q 280 110 300 140"
              fill="none"
              stroke="#f4a261"
              strokeWidth="2"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 0.8, duration: 0.5 }}
            />
          </motion.g>

          {/* Student */}
          <motion.g
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, type: 'spring' }}
          >
            {/* Body */}
            <circle cx="340" cy="170" r="18" fill="#fff" stroke="#1a1a1a" strokeWidth="2.5" />
            <line x1="340" y1="188" x2="340" y2="240" stroke="#1a1a1a" strokeWidth="2.5" />
            <line x1="320" y1="210" x2="360" y2="210" stroke="#1a1a1a" strokeWidth="2.5" />

            {/* Expression based on state */}
            {studentExpression === 'confused' ? (
              <>
                {/* Confused eyes */}
                <circle cx="333" cy="165" r="2" fill="#1a1a1a" />
                <circle cx="347" cy="165" r="2" fill="#1a1a1a" />
                {/* Question marks */}
                <motion.text
                  x="370"
                  y="140"
                  fontSize="20"
                  fill="#e63946"
                  animate={{ opacity: [0.4, 1, 0.4], y: [0, -5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                >?</motion.text>
                <motion.text
                  x="390"
                  y="130"
                  fontSize="16"
                  fill="#f4a261"
                  animate={{ opacity: [0.4, 1, 0.4], y: [0, -3, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5, delay: 0.3 }}
                >?</motion.text>
              </>
            ) : (
              <>
                {/* Happy eyes */}
                <motion.path
                  d="M 328 163 Q 333 168 338 163"
                  fill="none"
                  stroke="#1a1a1a"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                />
                <motion.path
                  d="M 342 163 Q 347 168 352 163"
                  fill="none"
                  stroke="#1a1a1a"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                />
                {/* Smile */}
                <motion.path
                  d="M 333 175 Q 340 180 347 175"
                  fill="none"
                  stroke="#1a1a1a"
                  strokeWidth="2"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                />
                {/* Lightbulb */}
                <motion.text
                  x="370"
                  y="140"
                  fontSize="24"
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', stiffness: 200 }}
                >💡</motion.text>
              </>
            )}
          </motion.g>

          {/* Thought/speech bubble */}
          <motion.g
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, type: 'spring' }}
          >
            <ellipse cx="420" cy="100" rx="70" ry="35" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
            <polygon points="380,120 360,140 390,130" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
            <line x1="382" y1="122" x2="360" y2="140" stroke="#fff" strokeWidth="4" />
            <text x="420" y="105" textAnchor="middle" fontSize="12" fill="#1a1a1a">
              I don't get it...
            </text>
          </motion.g>

          {/* Teacher figure (partial) */}
          <motion.g
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
          >
            <circle cx="480" cy="180" r="15" fill="#fff" stroke="#1a1a1a" strokeWidth="2" />
            <line x1="480" y1="195" x2="480" y2="240" stroke="#1a1a1a" strokeWidth="2" />
          </motion.g>
        </motion.svg>
      </div>

      {/* QUESTION TEXT */}
      <div className={styles.questionText}>
        {gamifiedQuestion?.situation}
      </div>

      {/* ANSWER OPTIONS - Bottom */}
      <div className={styles.optionsArea}>
        {options.map((option, index) => (
          <motion.button
            key={option.label}
            type="button"
            className={`${styles.choiceBtn} ${selected === option.label ? styles.selected : ''}`}
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: selected && selected !== option.label ? 0.3 : 1 }}
            transition={{ delay: 0.8 + index * 0.1, type: 'spring', stiffness: 100 }}
            whileHover={!selected ? { y: -2, borderColor: accent } : {}}
            whileTap={!selected ? { scale: 0.98 } : {}}
            onClick={() => handleSelect(option.label)}
            disabled={!!selected}
          >
            <span className={styles.choiceLabel}>{option.label}</span>
            <span className={styles.choiceText}>{option.actionText || option.text}</span>
          </motion.button>
        ))}
      </div>

      {/* Narration */}
      <p className={styles.narration}>{gamifiedQuestion?.narration}</p>

      <ConsequenceReveal
        visible={showOutcome}
        tone={selected === 'B' ? 'success' : 'warning'}
        message={selectedOption?.consequence || 'Response captured.'}
        duration={1700}
        onDone={() => {
          setShowOutcome(false);
          if (selected) onAnswer(selected);
        }}
      />
    </div>
  );
};

export default MisconceptionCatcherEngine;
